<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="bg-white rounded-lg shadow-lg p-8">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando credencial...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-white rounded-lg shadow-lg p-8">
      <div class="text-center">
        <div class="mb-4">
          <svg class="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">Error al cargar el documento</h2>
        <p class="text-gray-600">{{ error }}</p>
      </div>
    </div>

    <!-- Credencial Content -->
    <div v-else-if="credencialData && credencialData.Estatus === 'Activo'" class="bg-white rounded-lg shadow-lg">
      <div ref="credencialContent" class="p-8">

        <!-- Banner -->
        <div class="mb-8 text-center">
          <img
            src="/images/banner-escuelas.png"
            alt="Banner Escuelas"
            class="mx-auto max-w-full h-auto"
            @error="handleImageError"
          />
        </div>

        <!-- Título -->
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-900">Credencial de Estudiante</h1>
        </div>

        <!-- Tarjeta de credencial -->
        <div class="max-w-lg mx-auto mb-8">
          <div class="rounded-2xl overflow-hidden shadow-xl border border-blue-100">

            <!-- Cabecera de la credencial -->
            <div class="bg-blue-600 px-6 py-4 flex items-center space-x-4">
              <!-- Avatar / iniciales -->
              <div class="w-14 h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow">
                <span class="text-blue-600 font-bold text-xl">{{ initials }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-white text-xs font-medium uppercase tracking-wide opacity-80 mb-0.5">Nombre</p>
                <p class="text-white font-bold text-lg leading-tight">{{ credencialData.Nombre }}</p>
              </div>
              <!-- Indicador de estatus -->
              <span
                :class="[
                  'flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold',
                  credencialData.Estatus === 'Activo'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                ]"
              >
                {{ credencialData.Estatus }}
              </span>
            </div>

            <!-- Cuerpo de la credencial -->
            <div class="bg-white px-6 py-5 grid grid-cols-2 gap-x-6 gap-y-5">

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Matrícula</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Matricula }}</p>
              </div>

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Campus</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Campus }}</p>
              </div>

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Clave Institucional (CCT)</p>
                <p class="text-gray-900 font-semibold text-base font-mono tracking-wide">{{ credencialData.CCT }}</p>
              </div>

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Carrera</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Carrera }}</p>
              </div>

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Aseguradora</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Aseguradora }}</p>
              </div>

              <div>
                <p class="text-xs text-gray-500 font-medium mb-0.5">Póliza</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Póliza }}</p>
              </div>

              <div class="col-span-2">
                <p class="text-xs text-gray-500 font-medium mb-0.5">Vigencia</p>
                <p class="text-gray-900 font-semibold text-base">{{ credencialData.Vigencia }}</p>
              </div>
            </div>

            <!-- Pie de la credencial -->
            <div class="bg-gray-50 border-t border-gray-100 px-6 py-3 flex items-center justify-between">
              <div class="flex items-center space-x-1.5">
                <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                <span class="text-xs text-gray-500">Documento verificado</span>
              </div>
              <span class="text-xs text-gray-400 font-mono">{{ guid.substring(0, 10) }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Credencial Inactiva -->
    <div v-else-if="credencialData && credencialData.Estatus !== 'Activo'" class="bg-white rounded-lg shadow-lg p-8">
    <div class="text-center">
        <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
        </svg>
        <h2 class="text-xl font-bold text-gray-700 mb-2">Credencial no activa</h2>
        <p class="text-gray-500">Esta credencial no se encuentra activa en este momento.</p>
    </div>
    </div>

  </div>
</template>

<script>

export default {
  name: 'CredencialContent',
  props: {
    credencialData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    guid: {
      type: String,
      default: ''
    }
  },
  computed: {
    initials() {
      if (!this.credencialData?.Nombre) return '?';
      return this.credencialData.Nombre
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();
    }
  },
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none';
    },

  }
};
</script>